import {
  deleteTaskAppendix,
  deleteTasks,
  queryTasksV2,
  updateTask,
  uploadTaskAppendix,
} from '@/services/api/task.api';
import { getRecordDocument } from '@/services/api/record.api';
import { importTasksV2, queryTablesV2 } from '@/services/api/v2/connect_v2';

const state = {
  assemblyTasks: [],
  focusedTask: null,
  focusedHall: null,
  assemblyHalls: [],
  assemblyConfigurations: [],
  assemblyLocations: [],
  assemblyLanes: [],
  calendar: [],
  requestCounter: 0,
  projectModules: [],
  projectAssemblyTasks: [],
  projectModuleTableId: null,
  importCounter: 0,
};
const mutations = {
  make_request(state) {
    state.requestCounter++;
  },
  request_success(state) {
    state.requestCounter--;
  },
  assembly_tasks_success(state, tasks) {
    state.assemblyTasks = tasks;
  },
  update_assembly_tasks(state, tasks) {
    const list = Array.isArray(tasks) ? tasks : [];

    for (const t1 of list) {
      const idx = state.assemblyTasks.findIndex((t2) => t2.id === t1.id);

      if (idx === -1) {
        state.assemblyTasks.push(t1);
      } else {
        state.assemblyTasks[idx] = {
          ...state.assemblyTasks[idx],
          ...t1,
        };
      }
    }
  },
  focus_on_hall(state, hall) {
    state.focusedHall = hall;
  },
  focus_on_task(state, task) {
    state.focusedTask = task;
  },
  assembly_halls_success(
    state,
    { calendar, halls, configurations, locations, lanes }
  ) {
    state.calendar = calendar;
    state.assemblyHalls = halls;
    state.assemblyConfigurations = configurations;
    state.assemblyLocations = locations;
    state.assemblyLanes = lanes;
  },
  modules_success(state, { tableId, modules }) {
    state.projectModuleTableId = tableId;
    state.projectModules = modules;
  },
  project_module_assembly_tasks_success(state, { tasks }) {
    state.projectAssemblyTasks = tasks;
  },
};
const actions = {
  async fetchAssemblyTasks({ commit, rootGetters }) {
    commit('make_request');
    try {
      const response = await queryTasksV2(rootGetters.project.license, [
        {
          column: 'type',
          operator: '=',
          values: ['dhme-assembly-phase'],
        },
        {
          column: 'status',
          operator: '=',
          values: ['open'],
        },
      ]);
      commit('assembly_tasks_success', response.tasks);
    } catch (e) {
      this.commit('showNotification', {
        content: e.message,
        color: 'error',
      });
    } finally {
      commit('request_success');
    }
  },
  async fetchAssemblyHallsAndConfigurations({ commit, rootGetters }) {
    commit('make_request');
    try {
      const { assemblyHalls, calendar } = await queryTablesV2({
        tables: [
          {
            name: 'CFFA_DHME_ASSEMBLY_HALLS',
            project: rootGetters.project.id,
            as: 'assemblyHalls',
            columns: [
              {
                name: 'hall',
                as: 'title',
              },
              {
                name: 'automated',
              },
              {
                name: 'type',
                conditions: [
                  {
                    operator: '=',
                    value: 'assembly',
                  },
                ],
              },
            ],
          },
          {
            name: 'CFFA_DHME_CALENDAR',
            project: rootGetters.project.id,
            as: 'calendar',
            columns: [
              {
                name: 'label',
              },
              {
                name: 'date',
              },
            ],
          },
        ],
      });
      const { configurations, locations } = await queryTablesV2({
        tables: [
          {
            name: 'CFFA_DHME_ASSEMBLY_LOCATIONS',
            project: rootGetters.project.id,
            as: 'locations',
            sortBy: 'order',
            columns: [
              {
                name: 'assembly_hall',
                conditions: assemblyHalls.records.map((r) => {
                  return {
                    operator: '=',
                    value: r.id,
                    boolean: 'or',
                  };
                }),
              },
              {
                name: 'assembly_lane',
              },
              {
                name: 'assembly_location',
              },
              {
                name: 'description',
              },
              {
                name: 'order',
              },
              {
                name: 'hall_configuration',
                as: 'configuration',
              },
            ],
          },
          {
            name: 'CFFA_DHME_ASSEMBLY_HALL_CONFIGURATIONS',
            project: rootGetters.project.id,
            as: 'configurations',
            columns: [
              {
                name: 'assembly_hall',
                conditions: assemblyHalls.records.map((r) => {
                  return {
                    operator: '=',
                    value: r.id,
                    boolean: 'or',
                  };
                }),
              },
              {
                name: 'title',
              },
            ],
          },
        ],
      });

      const { lanes } = await queryTablesV2({
        tables: [
          {
            name: 'CFFA_DHME_ASSEMBLY_LANES',
            project: rootGetters.project.id,
            as: 'lanes',
            sortBy: 'order',
            columns: [
              {
                name: 'title',
              },
              {
                name: 'color',
              },
              {
                name: 'order',
              },
              {
                name: 'configuration',
                conditions: [
                  {
                    operator: '=',
                    values: configurations.records.map((c) => c.id),
                  },
                ],
              },
            ],
          },
        ],
      });

      commit('assembly_halls_success', {
        calendar: calendar.records,
        halls: assemblyHalls.records,
        configurations: configurations.records,
        locations: locations.records,
        lanes: lanes.records,
      });
    } catch (e) {
      this.commit('showNotification', {
        content: e.message,
        color: 'error',
      });
    } finally {
      commit('request_success');
    }
  },
  async fetchModulesInOrder({ commit, rootGetters, getters }) {
    commit('make_request');
    try {
      const { modules } = await queryTablesV2({
        tables: [
          {
            name: 'CFFA_DHME_MODULES',
            project: getters.focusedTask?.task_project.id,
            as: 'modules',
            sortBy: 'assembly_sequence',
            columns: [
              {
                name: 'module_id',
              },
              {
                name: 'module_type',
              },
              {
                name: 'build_nr',
              },
              {
                name: 'assembly_sequence',
              },
              {
                name: 'assembly_workload',
              },
              {
                name: '2d_drawing',
              },
            ],
          },
        ],
      });

      commit('modules_success', {
        tableId: modules.id,
        modules: modules.records,
      });

      const { tasks } = await queryTasksV2(rootGetters.project.license, [
        {
          column: 'type',
          operator: '=',
          values: ['dhme-module-assembly'],
        },
        {
          column: 'project',
          operator: '=',
          values: [getters.focusedTask?.task_project?.id],
        },
      ]);
      commit('project_module_assembly_tasks_success', {
        tasks: tasks,
      });
    } catch (e) {
      this.commit('showNotification', {
        content: e.message,
        color: 'error',
      });
    } finally {
      commit('request_success');
    }
  },

  // A re-plan starts from a clean slate: station subtasks belong to the old
  // locations and module tasks of removed modules have nothing left to plan.
  // Every check runs before the first delete, so a refusal changes nothing.
  async clearPreviousPlanning({ commit, rootGetters, getters }, { hallId }) {
    const moduleTasks = getters.projectAssemblyTasks || [];
    if (moduleTasks.length === 0) return;

    const hallChanged = moduleTasks.some(
      (t) => t.task_type.custom_2 && t.task_type.custom_2 !== hallId
    );
    const assembled = moduleTasks.filter((t) => t.status === 'closed');
    if (hallChanged && assembled.length > 0) {
      throw new Error(
        `Hal wisselen is niet mogelijk: ${assembled.length} module(s) zijn al geassembleerd.`
      );
    }

    const { tasks: subtasks } = await queryTasksV2(
      rootGetters.project.license,
      [
        {
          column: 'parent',
          operator: '=',
          values: moduleTasks.map((t) => t.id),
        },
      ]
    );
    const doneSubtasks = (subtasks || []).filter((t) => t.status === 'closed');
    if (doneSubtasks.length > 0) {
      throw new Error(
        `${doneSubtasks.length} assemblagevoorbereiding(en) zijn al afgerond. Verwijder deze taken en plan daarna opnieuw.`
      );
    }

    const buildNrs = new Set(getters.projectModules.map((m) => m.build_nr));
    const orphans = moduleTasks.filter((t) => !buildNrs.has(t.sbscode?.code));

    const toDelete = [...(subtasks || []), ...orphans];
    if (toDelete.length > 0) {
      await deleteTasks(
        toDelete.map((t) => ({ id: t.id, method: 'with-subtasks' }))
      );
    }

    if (orphans.length > 0) {
      const orphanIds = new Set(orphans.map((t) => t.id));
      commit('project_module_assembly_tasks_success', {
        tasks: moduleTasks.filter((t) => !orphanIds.has(t.id)),
      });
    }
  },

  async importAssemblyTasks(
    { commit, rootGetters, getters, state, dispatch },
    { tasks, assemblyPhase }
  ) {
    commit('make_request');

    const projectId = getters.focusedTask?.task_project?.id;
    const licenseId = rootGetters.project?.license;

    if (!projectId || !licenseId) {
      commit('request_success');
      this.commit('showNotification', {
        content: 'Planning failed: project or license context is missing.',
        color: 'error',
      });
      return;
    }

    function chunkArray(array, size) {
      const chunks = [];
      for (let i = 0; i < array.length; i += size)
        chunks.push(array.slice(i, i + size));
      return chunks;
    }

    try {
      // 1) Import tasks (do not rely on return value anymore)
      const taskChunks = chunkArray(tasks, 49);
      let importedCount = 0;
      for (const chunk of taskChunks) {
        try {
          await importTasksV2(chunk);
          importedCount += chunk.length;
        } catch (chunkError) {
          throw new Error(
            `Import gestopt na ${importedCount} van ${tasks.length} modules. Controleer de data en probeer opnieuw. (${chunkError.message})`
          );
        }
      }

      await updateTask(assemblyPhase.id, assemblyPhase);

      // 2) Refetch module assembly tasks after import (source of truth)
      const { tasks: fetchedTasks } = await queryTasksV2(licenseId, [
        {
          column: 'type',
          operator: '=',
          values: ['dhme-module-assembly'],
        },
        {
          column: 'project',
          operator: '=',
          values: [projectId],
        },
      ]);

      // 3) Fast lookup by sbscode.code (module build_nr)
      const taskBySbs = new Map();
      for (const t of fetchedTasks || []) {
        const code = t?.sbscode?.code;
        if (code) taskBySbs.set(code, t);
      }

      // 4) Upload drawings per module-task
      state.importCounter = 0;
      let drawingFailures = 0;

      const modulesWithDrawing = (getters.projectModules || []).filter(
        (m) => m?.['2d_drawing']
      );

      for (const m of modulesWithDrawing) {
        const task = taskBySbs.get(m.build_nr);

        // If task not found, skip but still progress counter
        if (!task) {
          state.importCounter++;
          continue;
        }

        try {
          const appendixName = `2D_${m.module_id}`;
          const existing = (task.appendixes || []).find(
            (a) => a.name === appendixName
          );

          if (existing) {
            await deleteTaskAppendix(task.id, existing.id);
          }

          const document = await getRecordDocument(
            m['2d_drawing']?.id,
            projectId,
            getters.projectModuleTableId
          );

          if (document?.file) {
            await uploadTaskAppendix(task.id, {
              data: document.file,
              extension: document.extension,
              name: appendixName,
            });
          }
        } catch (drawingError) {
          drawingFailures++;
        }

        state.importCounter++;
      }

      // Keep store in sync with what we just fetched
      commit('update_assembly_tasks', fetchedTasks || []);

      // Refresh the module-assembly task list used for dedup on the next plan.
      // Without this, a back-to-back replan matches against a stale/empty list
      // and re-creates tasks instead of updating them (duplicate tasks).
      commit('project_module_assembly_tasks_success', {
        tasks: fetchedTasks || [],
      });

      dispatch('fetchAssemblyTasks');

      if (drawingFailures > 0) {
        this.commit('showNotification', {
          content: `Planning saved, but ${drawingFailures} drawing(s) could not be uploaded.`,
          color: 'warning',
        });
      } else {
        this.commit('showNotification', {
          content: `Assembly planning saved successfully for ${tasks.length} module(s).`,
          color: 'success',
        });
      }
    } catch (e) {
      this.commit('showNotification', {
        content: `Failed to save planning: ${e.message}`,
        color: 'error',
      });
      throw e;
    } finally {
      commit('request_success');
    }
  },

  async rejectPlanning({ commit, getters, dispatch, state }, { tasks }) {
    commit('make_request');
    try {
      const result = await importTasksV2(tasks);
      const importedTasks = Array.isArray(result) ? result : [result];
      commit('update_assembly_tasks', importedTasks);
      this.commit('showNotification', {
        content: 'Planning rejected.',
        color: 'info',
      });
    } catch (e) {
      this.commit('showNotification', {
        content: `Failed to reject planning: ${e.message}`,
        color: 'error',
      });
    } finally {
      commit('request_success');
    }
  },
};
const getters = {
  requestedAssemblyTasks: (state) =>
    state.assemblyTasks.filter(
      (t) => t.task_type.custom_1 === 'request' && t.status === 'open'
    ),
  plannedAssemblyTasks: (state) =>
    state.assemblyTasks.filter((t) => t.task_type.custom_1 === 'planned'),
  focusedTask: (state) => state.focusedTask,
  focusedHall: (state) => state.focusedHall,
  assemblyHalls: (state) => state.assemblyHalls,
  assemblyConfigurations: (state) => state.assemblyConfigurations,
  assemblyLocations: (state) => state.assemblyLocations,
  assemblyLanes: (state) => state.assemblyLanes,
  vacationCalendar: (state) => state.calendar,
  isLoading: (state) => state.requestCounter > 0,
  projectModules: (state) => state.projectModules,
  projectAssemblyTasks: (state) => state.projectAssemblyTasks,
  projectModuleTableId: (state) => state.projectModuleTableId,
  importCounter: (state) => state.importCounter,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
